import { z } from "zod";
import { accessibleName, computeRole } from "./roles.js";
import { isToolError } from "./types.js";
/** Zod schemas for every tool — used by the model provider and to validate input. */
export const toolSchemas = {
    read_page: z.object({
        scope: z.string().optional().describe("ref to snapshot a subtree, e.g. 'e7'"),
        mode: z.enum(["full", "interactive"]).optional(),
        maxNodes: z.number().int().positive().optional(),
    }),
    query: z.object({
        testid: z.string().optional(),
        role: z.string().optional(),
        name: z.string().optional().describe("accessible-name substring"),
        text: z.string().optional().describe("visible text substring"),
        limit: z.number().int().positive().optional(),
    }),
    read_element: z.object({ ref: z.string() }),
    click: z.object({ ref: z.string() }),
    type: z.object({
        ref: z.string(),
        text: z.string(),
        mode: z.enum(["replace", "append"]).optional(),
        pressEnter: z.boolean().optional(),
    }),
    select: z.object({
        ref: z.string(),
        option: z.object({ value: z.string().optional(), label: z.string().optional() }),
    }),
    scroll: z.object({
        ref: z.string().optional(),
        direction: z.enum(["up", "down"]).optional(),
    }),
    wait_for: z.object({
        appear: z.object({ testid: z.string().optional(), role: z.string().optional(), name: z.string().optional(), text: z.string().optional() }).optional(),
        disappear: z.object({ testid: z.string().optional(), role: z.string().optional(), name: z.string().optional(), text: z.string().optional() }).optional(),
        urlChange: z.boolean().optional(),
        timeoutMs: z.number().int().positive().optional(),
    }),
    ask_user: z.object({
        question: z.string(),
        kind: z.enum(["question", "confirm"]).optional(),
    }),
};
export const toolDescriptions = {
    read_page: "Return a compact accessibility snapshot of the page (roles, names, states, refs). Prefer 'interactive' mode; scope to a ref to zoom in.",
    query: "Find elements by data-testid, role+name, or visible text. Returns refs. Ambiguity returns multiple candidates.",
    read_element: "Read full detail of one element by ref: value, states, error text, whether it's in view.",
    click: "Click an element by ref. Sensitive clicks (submit/pay/delete/external) are gated by a user confirmation.",
    type: "Type text into an input/textarea/contenteditable by ref. Never types into password fields.",
    select: "Choose an option in a <select>, listbox, or radio group by ref.",
    scroll: "Scroll the page, or scroll a ref into view.",
    wait_for: "Wait (bounded) for an element to appear/disappear or the URL to change after an action.",
    ask_user: "Ask the user a question or request confirmation, then continue with their answer.",
};
/** Executes a validated tool call and returns a JSON-serializable result. */
export class ToolExecutor {
    ctx;
    constructor(ctx) {
        this.ctx = ctx;
    }
    async execute(name, rawInput) {
        const schema = toolSchemas[name];
        const parsed = schema.safeParse(rawInput ?? {});
        if (!parsed.success) {
            return { error: { code: "not_found", message: `invalid input: ${parsed.error.message}` } };
        }
        const input = parsed.data;
        switch (name) {
            case "read_page": {
                const r = this.ctx.snapshot.snapshot({
                    scope: input.scope,
                    mode: input.mode ?? "interactive",
                    maxNodes: input.maxNodes,
                });
                return { snapshot: r.text, truncated: r.truncated };
            }
            case "query": {
                const hits = this.ctx.actions.query({ ...input, root: this.ctx.root() });
                if (hits.length === 0)
                    return { error: { code: "not_found", message: "no matching elements" } };
                return { hits };
            }
            case "read_element":
                return this.readElement(input.ref);
            case "click":
                return this.ctx.actions.click(input.ref);
            case "type":
                return this.ctx.actions.type(input.ref, input.text, input.mode ?? "replace", Boolean(input.pressEnter));
            case "select":
                return this.ctx.actions.select(input.ref, input.option);
            case "scroll":
                return this.ctx.actions.scroll({
                    ref: input.ref,
                    direction: input.direction,
                    root: this.ctx.root(),
                });
            case "wait_for":
                return this.waitFor(input);
            case "ask_user": {
                const answer = await this.ctx.confirm({
                    question: input.question,
                    kind: input.kind ?? "question",
                });
                return { answer };
            }
        }
    }
    readElement(ref) {
        const el = this.ctx.registry.resolve(ref);
        if (!el)
            return { error: { code: "stale_ref", message: "element no longer exists" } };
        const role = computeRole(el);
        const name = accessibleName(el);
        const input = el;
        const value = input.type === "password" ? undefined : input.value;
        const errId = el.getAttribute("aria-errormessage") ?? el.getAttribute("aria-describedby");
        let errorText;
        if (errId) {
            const doc = el.ownerDocument;
            errorText = errId
                .split(/\s+/)
                .map((id) => doc.getElementById(id)?.textContent?.trim())
                .filter(Boolean)
                .join(" ") || undefined;
        }
        const rect = el.getBoundingClientRect?.();
        const inViewport = rect
            ? rect.top >= 0 && rect.bottom <= (el.ownerDocument.defaultView?.innerHeight ?? Infinity)
            : undefined;
        return { ref, role, name: name || undefined, value, errorText, inViewport };
    }
    async waitFor(input) {
        const timeout = input.timeoutMs ?? 5000;
        const start = this.ctx.now();
        const startUrl = this.ctx.root().ownerDocument.location.href;
        const check = () => {
            if (input.urlChange && this.ctx.root().ownerDocument.location.href !== startUrl)
                return true;
            if (input.appear) {
                const hits = this.ctx.actions.query({ ...input.appear, root: this.ctx.root(), limit: 1 });
                if (hits.length > 0)
                    return true;
            }
            if (input.disappear) {
                const hits = this.ctx.actions.query({ ...input.disappear, root: this.ctx.root(), limit: 1 });
                if (hits.length === 0)
                    return true;
            }
            return false;
        };
        while (this.ctx.now() - start < timeout) {
            if (check())
                return { ok: true, matched: true };
            await sleep(100);
        }
        return { error: { code: "timeout", message: "condition not met within timeout" } };
    }
}
/** Turn a tool result into a one-line summary for the activity feed. */
export function summarizeResult(name, result) {
    if (isToolError(result))
        return { ok: false, summary: `${name}: ${result.error.code} — ${result.error.message}` };
    const r = result;
    if (name === "read_page")
        return { ok: true, summary: `read page (${r.truncated ? "truncated" : "full"})` };
    if (name === "query")
        return { ok: true, summary: `found ${r.hits.length} element(s)` };
    if (r.delta)
        return { ok: true, summary: String(r.delta) };
    if (r.answer)
        return { ok: true, summary: `user: ${String(r.answer).slice(0, 40)}` };
    return { ok: true, summary: `${name} ok` };
}
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
//# sourceMappingURL=tools.js.map